'use client';

import { memo } from 'react';

import {
  Brain,
  ChevronLeft,
  Heart,
  Palette,
  Sparkles,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Trans } from '@kit/ui/trans';

import { type CharacterConfig } from './types';

interface Step6VerificationProps {
  config: CharacterConfig;
  onBack: () => void;
  onGenerate: () => void;
  isGenerating: boolean;
  coinCost: number;
}

export const Step6Verification = memo(function Step6Verification({
  config,
  onBack,
  onGenerate,
  isGenerating,
  coinCost,
}: Step6VerificationProps) {
  const { t } = useTranslation('common');

  // Format display values
  const formatValue = (value: string | undefined) => {
    if (!value) return t('characterCreator.valueNotSet');
    return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
  };

  const getStyleLabel = () => {
    switch (config.style) {
      case 'realistic':
        return t('characterCreator.styleLabelRealistic');
      case 'anime':
        return t('characterCreator.styleLabelAnime');
      case 'furry':
        return t('characterCreator.styleLabelFurry');
      default:
        return config.style;
    }
  };

  const sections = [
    {
      title: t('characterCreator.sectionBasic'),
      icon: User,
      items: [
        {
          label: t('characterCreator.fieldName'),
          value: config.name || t('characterCreator.valueUnnamed'),
        },
        {
          label: t('characterCreator.fieldAge'),
          value: t('characterCreator.valueYearsOld', { age: config.age }),
        },
        {
          label: t('characterCreator.fieldGender'),
          value: formatValue(config.gender),
        },
        { label: t('characterCreator.fieldStyle'), value: getStyleLabel() },
      ],
    },
    {
      title: t('characterCreator.sectionAppearance'),
      icon: Palette,
      items: [
        ...(config.style !== 'furry'
          ? [
              {
                label: t('characterCreator.fieldEthnicity'),
                value: formatValue(config.ethnicity),
              },
              {
                label: t('characterCreator.fieldSkinTone'),
                value: formatValue(config.skinTone),
              },
            ]
          : [
              {
                label: t('characterCreator.fieldSpecies'),
                value: formatValue(config.species) || 'Wolf',
              },
              {
                label: t('characterCreator.fieldFurColor'),
                value:
                  formatValue(config.furColor) || formatValue(config.hairColor),
              },
            ]),
        {
          label: t('characterCreator.fieldEyeColor'),
          value: formatValue(config.eyeColor),
        },
        ...(config.style !== 'furry'
          ? [
              {
                label: t('characterCreator.fieldHairColor'),
                value: formatValue(config.hairColor),
              },
              {
                label: t('characterCreator.fieldHairStyle'),
                value: formatValue(config.hairStyle),
              },
            ]
          : []),
      ],
    },
    {
      title: t('characterCreator.sectionBody'),
      icon: Heart,
      items: [
        {
          label: t('characterCreator.fieldBodyType'),
          value: formatValue(config.bodyType),
        },
        ...(config.gender !== 'male'
          ? [
              {
                label: t('characterCreator.fieldChestSize'),
                value: formatValue(config.chestSize),
              },
            ]
          : []),
        {
          label: t('characterCreator.fieldBodyShape'),
          value: formatValue(config.bodyShape),
        },
        ...(config.gender !== 'female' && config.dickSize
          ? [
              {
                label: t('characterCreator.fieldDickSize'),
                value: formatValue(config.dickSize),
              },
            ]
          : []),
      ],
    },
    {
      title: t('characterCreator.sectionPersonality'),
      icon: Brain,
      items: [
        {
          label: t('characterCreator.sectionPersonality'),
          value: config.personality || t('characterCreator.valueNotSet'),
        },
        {
          label: t('characterCreator.fieldRelationship'),
          value: formatValue(config.relationshipStatus),
        },
        ...(config.occupation
          ? [
              {
                label: t('characterCreator.fieldOccupation'),
                value: config.occupation,
              },
            ]
          : []),
        ...(config.voice
          ? [{ label: t('characterCreator.fieldVoice'), value: config.voice }]
          : []),
      ],
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-2 px-2 sm:space-y-3 sm:px-4 lg:px-0">
      {/* Title */}
      <div className="mb-1 text-center sm:mb-2">
        <h2 className="mb-0.5 text-lg font-bold text-white sm:mb-1 sm:text-xl md:text-2xl">
          <Trans i18nKey="common:characterCreator.verifyTitle" />
        </h2>
        <p className="bg-gradient-to-r from-red-400 via-pink-400 to-pink-600 bg-clip-text text-[10px] font-semibold text-transparent sm:text-xs md:text-sm">
          <Trans i18nKey="common:characterCreator.verifySubtitle" />
        </p>
      </div>

      {/* Character Summary Grid */}
      <div className="grid grid-cols-2 gap-1.5 sm:gap-2 md:gap-3">
        {sections.map((section) => (
          <div
            key={section.title}
            className="rounded-lg border border-gray-800 bg-gray-900/50 p-2 sm:rounded-xl sm:p-3 md:p-4"
          >
            <div className="mb-1.5 flex items-center gap-1 sm:mb-2 sm:gap-2">
              <section.icon className="h-3 w-3 text-pink-500 sm:h-4 sm:w-4" />
              <h3 className="text-[10px] font-semibold text-white sm:text-xs md:text-sm">
                {section.title}
              </h3>
            </div>
            <div className="space-y-1 sm:space-y-1.5">
              {section.items.map((item, idx) => (
                <div
                  key={idx}
                  className="flex justify-between text-[9px] sm:text-[10px] md:text-xs"
                >
                  <span className="text-gray-400">{item.label}</span>
                  <span className="font-medium text-white">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Personality Traits */}
      {config.personalityTraits && config.personalityTraits.length > 0 && (
        <div className="rounded-lg border border-gray-800 bg-gray-900/50 p-2 sm:rounded-xl sm:p-3 md:p-4">
          <h3 className="mb-1.5 text-[10px] font-semibold text-white sm:mb-2 sm:text-xs md:text-sm">
            <Trans i18nKey="common:characterCreator.personalityTraits" />
          </h3>
          <div className="flex flex-wrap gap-1 sm:gap-1.5">
            {config.personalityTraits.map((trait) => (
              <span
                key={trait}
                className="rounded-full bg-pink-600/20 px-2 py-0.5 text-[9px] text-pink-300 sm:px-3 sm:py-1 sm:text-[10px] md:text-xs"
              >
                {formatValue(trait)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Cost Info */}
      <div className="rounded-lg border border-orange-500/30 bg-gradient-to-r from-orange-600/20 to-pink-600/20 p-2 text-center sm:rounded-xl sm:p-3 md:p-4">
        <div className="mb-0.5 flex items-center justify-center gap-1.5 sm:mb-1 sm:gap-2">
          <Sparkles className="h-3.5 w-3.5 text-orange-400 sm:h-4 sm:w-4 md:h-5 md:w-5" />
          <span className="text-sm font-bold text-white sm:text-base md:text-lg">
            {t('characterCreator.coinsCost', { count: coinCost })}
          </span>
        </div>
        <p className="text-[9px] text-gray-400 sm:text-[10px] md:text-xs">
          <Trans i18nKey="common:characterCreator.willBeCharged" />
        </p>
      </div>

      {/* Navigation Buttons */}
      <div className="mt-2 flex justify-center gap-2 sm:mt-3 sm:gap-2.5">
        <button
          onClick={onBack}
          disabled={isGenerating}
          className="flex items-center gap-1 rounded-lg border border-white/30 bg-transparent px-4 py-1.5 text-[10px] font-semibold text-white transition-all hover:border-white/50 hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-1.5 sm:border-2 sm:px-6 sm:py-2 sm:text-xs md:px-8"
        >
          <ChevronLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
          <Trans i18nKey="common:characterCreator.backButton" />
        </button>
        <button
          onClick={onGenerate}
          disabled={isGenerating || !config.name.trim()}
          className="flex items-center gap-1 rounded-lg bg-gradient-to-r from-pink-600 to-orange-500 px-5 py-1.5 text-[10px] font-bold text-white shadow-lg shadow-pink-500/30 transition-all hover:from-pink-500 hover:to-orange-400 disabled:cursor-not-allowed disabled:opacity-50 sm:gap-1.5 sm:px-8 sm:py-2 sm:text-xs md:px-10"
        >
          {isGenerating ? (
            <>
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-white/30 border-t-white sm:h-3.5 sm:w-3.5" />
              <Trans i18nKey="common:characterCreator.generating" />
            </>
          ) : (
            <>
              <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <Trans i18nKey="common:characterCreator.generateButton" />
            </>
          )}
        </button>
      </div>

      {/* Missing name warning */}
      {!config.name.trim() && (
        <p className="text-center text-[10px] text-red-400 sm:text-xs">
          <Trans i18nKey="common:characterCreator.missingName" />
        </p>
      )}
    </div>
  );
});
